import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetTerminalCardQuery } from 'app/api/facade';
import { glToStringHDMS } from 'app/helpers';
import { useAppSelector } from 'app/store/store';
import {
  InfoWrapper,
  LocalTime,
  Icon,
  VesselsCount
} from 'app/components';
import { CountryFlag } from 'CountryFlag/CountryFlag'

import styles from './TerminalCard.module.scss';

interface Props {
  terminalKey: string;
  shown: boolean;
  isHistoryMode?: boolean;
  onClose: () => void;
  onClick: () => void;
  toggleVesselsCatalog: () => void;
}
export const TerminalCard = ({ terminalKey, onClose, onClick, shown, isHistoryMode, toggleVesselsCatalog }: Props) => {
  const { t } = useTranslation();

  const profile = useAppSelector((state) => state.userSlice.profile);
  const coordinatesFormat = useMemo(() => profile?.ui?.['coordinatesFormat'] ?? 'DMS', [profile]);

  const { data: terminalData } = useGetTerminalCardQuery(terminalKey, { skip: !terminalKey });

  const { lat, lon, name, mmsiCount, port, description, code } = terminalData?.result || {};

  const position: string | undefined = useMemo(() => {
    if (typeof lat !== 'number' || typeof lon !== 'number') {
      return undefined;
    }
    return glToStringHDMS([lon, lat], coordinatesFormat);
  }, [lat, lon, coordinatesFormat]);

  return (
    <>
      {shown && terminalData && (
        <div className={styles.wrapper}>
          <div className={styles.header}>
            <div className={styles.name}>
              <Icon name="terminal" color="none" />
              <span>{code}</span>
            </div>

            <div onClick={onClose} className={styles.close}>
              <Icon name="close-16" size="16px" />
            </div>
          </div>

          <div className={styles.sub_header}>{t('Terminal')}</div>
          <span className={styles.description}>{description}</span>
          <div className={styles.sub_header} />
          {port && (
            <div className={styles.content}>
              <InfoWrapper
                label={`${t('Country')}:`}
                value={
                  typeof port.countryCode !== 'undefined' ? (
                    <>
                      {port.countryCode && <CountryFlag countryCode={port.countryCode.toLowerCase()} />}
                      <strong>
                        {port.meta?.countryName} ({port.countryCode})
                      </strong>
                    </>
                  ) : (
                    <div className={styles.no_data}>&mdash;</div>
                  )
                }
              />
              <InfoWrapper label={`${t('Port')}`} strong value={port ? port.name : undefined} />
              <InfoWrapper label="UN/LOCODE:" strong value={`${port.un} ${port.locode}`} />
              <InfoWrapper label={`${t('LocalTime')}:`} value={<LocalTime lat={lat} lon={lon} />} />
              <InfoWrapper
                label={`${t('Coordinates')}:`}
                value={position}
                valueIcon={<Icon name="aim" size="14px" />}
                clickable={!!position}
                onClick={onClick}
              />
              {!isHistoryMode && (
                <InfoWrapper
                  label={`${t('TerminalTargetCatalog')}:`}
                  onClick={toggleVesselsCatalog}
                  clickable={Boolean(mmsiCount && mmsiCount > 0)}
                  value={<VesselsCount mmsiCount={mmsiCount} />}
                />
              )}
              <div className={styles.divider} />
            </div>
          )}
          <span>{name}</span>
        </div>
      )}
    </>
  );
};
